import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Confetti from 'react-confetti';
import { useAuth } from '../context/AuthContext';
import { checkersAPI } from '../services/api';
import { checkersDuelSocket, getSocket } from '../services/socket';
import { formatSquare, getLegalMoves } from '../checkers/engine';
import { notifyPlayerTurn } from '../utils/turnAlerts';
import { buildInviteLink } from '../utils/inviteLinks';
import {
  getGlobalSoundEnabled,
  setGlobalSoundEnabled,
  subscribeToGlobalSoundPreference,
} from '../utils/soundPreferences';
import '../styles/Flappy.css';
import '../styles/Checkers.css';
import QuickChat from '../components/QuickChat';

function asNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatTimer(ms) {
  const safe = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function pieceMeta(piece) {
  if (!piece) return null;
  const lower = String(piece).toLowerCase();
  const color = lower === 'r' ? 'red' : 'black';
  const king = piece === 'R' || piece === 'B';
  return { color, king };
}

export default function CheckersMatch() {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user, refreshUser } = useAuth();

  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showWinnerConfetti, setShowWinnerConfetti] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [rematchCandidate, setrematchCandidate] = useState(null);
  const [rematchBusy, setrematchBusy] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return getGlobalSoundEnabled();
  });
  const [tick, setTick] = useState(Date.now());
  const lastServerOffsetRef = useRef(0);
  const lastTurnTokenRef = useRef('');
  const inviteJoinAttemptedRef = useRef(false);

  const meId = asNumber(user?.id);
  const myColor = useMemo(() => {
    if (!match || !meId) return null;
    if (asNumber(match.red_id) === meId) return 'r';
    if (asNumber(match.black_id) === meId) return 'b';
    return null;
  }, [match, meId]);

  const isMyTurn = match?.status === 'in_progress' && myColor && match.turn === myColor;

  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    setGlobalSoundEnabled(soundEnabled);
  }, [soundEnabled]);

  useEffect(() => subscribeToGlobalSoundPreference(setSoundEnabled), []);

  const effectiveServerNow = tick + (lastServerOffsetRef.current || 0);
  const turnStartedAt = match?.turn_started_at ? new Date(match.turn_started_at).getTime() : null;
  const turnTimeoutMs = asNumber(match?.turn_timeout_seconds) * 1000;
  const turnDeadlineMs = turnStartedAt && turnTimeoutMs > 0 ? turnStartedAt + turnTimeoutMs : null;
  const turnTimeLeftMs = turnDeadlineMs ? Math.max(0, turnDeadlineMs - effectiveServerNow) : null;

  const board = useMemo(() => {
    if (!Array.isArray(match?.board_state)) return [];
    return match.board_state;
  }, [match?.board_state]);

  const legalMoves = useMemo(() => {
    if (!board.length || !match?.status || match.status !== 'in_progress') return [];
    const forced = match?.must_continue_from ? String(match.must_continue_from) : null;
    return getLegalMoves(board, String(match.turn || 'r'), forced);
  }, [board, match?.status, match?.turn, match?.must_continue_from]);

  const legalTargets = useMemo(() => {
    if (!selectedSquare) return [];
    return legalMoves.filter((m) => m.from === selectedSquare).map((m) => m.to);
  }, [legalMoves, selectedSquare]);

  const load = async () => {
    try {
      setError('');
      const mRes = await checkersAPI.getMatch(matchId);
      const m = mRes.data;
      setMatch(m);
      setIsReconnecting(false);
      if (typeof m?.server_now_ms === 'number') {
        lastServerOffsetRef.current = m.server_now_ms - Date.now();
      }
    } catch (e) {
      if (!e?.response) {
        setIsReconnecting(true);
        return;
      }
      setError(e.response?.data?.error || 'Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      void load();
    }, 1500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    if (!matchId) return;
    checkersDuelSocket.join(matchId);

    const onUpdate = (payload = {}) => {
      const updated = payload?.match;
      if (!updated) return;
      if (String(updated.id) !== String(matchId)) return;
      setIsReconnecting(false);
      setMatch(updated);
      if (typeof updated?.server_now_ms === 'number') {
        lastServerOffsetRef.current = updated.server_now_ms - Date.now();
      }
    };

    checkersDuelSocket.onMatchUpdate(onUpdate);

    return () => {
      checkersDuelSocket.removeListeners();
    };
  }, [matchId]);

  useEffect(() => {
    const handleOffline = () => setIsReconnecting(true);
    const handleOnline = () => {
      setIsReconnecting(false);
      void load();
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    if (match?.status === 'completed') refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.status]);

  useEffect(() => {
    if (!match?.id || !myColor) return;
    if (match.status !== 'in_progress') return;

    const turnToken = `${match.id}:${match.turn}:${match.turn_started_at || ''}`;

    if (!lastTurnTokenRef.current) {
      lastTurnTokenRef.current = turnToken;
      return;
    }

    if (turnToken === lastTurnTokenRef.current) return;

    lastTurnTokenRef.current = turnToken;
    if (match.turn === myColor) {
      notifyPlayerTurn('Checkers');
    }
  }, [match?.id, match?.status, match?.turn, match?.turn_started_at, myColor]);

  const isParticipant = !!(match && (asNumber(match.creator_id) === meId || asNumber(match.opponent_id) === meId));
  const canAccept = !!(match && match.status === 'pending' && asNumber(match.opponent_id) === meId && !match.accepted_at);
  const canStart = !!(match && match.status === 'pending' && asNumber(match.creator_id) === meId && match.opponent_id && match.accepted_at);
  const canCancel = !!(match && match.status === 'pending' && asNumber(match.creator_id) === meId);
  const canResign = !!(match && match.status === 'in_progress' && isParticipant);
  const canJoinOpenRoom = !!(match && match.status === 'pending' && !match.opponent_id && !isParticipant);
  const isRoomFullForViewer = !!(match && match.status === 'pending' && match.opponent_id && !isParticipant && !canAccept);
  const inviteLink = match?.id ? buildInviteLink(`/checkers/duel/${match.id}`) : '';

  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        window.prompt('Copy Checkers invite link:', inviteLink);
      }
    } catch {
      window.prompt('Copy Checkers invite link:', inviteLink);
    }
  };

  const winnerId = asNumber(match?.winner_id);
  const didIWin = Boolean(match?.status === 'completed' && winnerId && winnerId === meId);
  const didILose = Boolean(match?.status === 'completed' && winnerId && winnerId !== meId && isParticipant);
  const redName = match?.red_username || match?.creator_username || 'Red';
  const blackName = match?.black_username || match?.opponent_username || 'Black';
  const topPlayerName = isReconnecting ? `Reconnecting... ${blackName}` : blackName;
  const bottomPlayerName = isReconnecting ? `Reconnecting... ${redName}` : redName;
  const turnTimeout = asNumber(match?.turn_timeout_seconds);
  const isCompleted = match?.status === 'completed';
  const isInProgress = match?.status === 'in_progress';
  const isPending = match?.status === 'pending';

  const RematchOpponentId = useMemo(() => {
    if (!match || !meId) return null;
    if (asNumber(match.red_id) === meId) return asNumber(match.black_id);
    if (asNumber(match.black_id) === meId) return asNumber(match.red_id);
    return null;
  }, [match, meId]);

  const RematchMode = String(match?.mode || (asNumber(match?.wager) > 0 ? 'betting' : 'friendly'));
  const RematchWager = asNumber(match?.wager);
  const RematchTimeControlSeconds = asNumber(match?.turn_timeout_seconds) || 30;

  const canAcceptRematch = Boolean(
    rematchCandidate
    && asNumber(rematchCandidate.opponent_id) === meId
    && !rematchCandidate.accepted_at
  );

  const canCreateRematch = Boolean(isCompleted && isParticipant && !rematchCandidate);

  useEffect(() => {
    if (!didIWin) {
      setShowWinnerConfetti(false);
      return undefined;
    }
    setShowWinnerConfetti(true);
    const timer = window.setTimeout(() => setShowWinnerConfetti(false), 4500);
    return () => window.clearTimeout(timer);
  }, [didIWin, match?.id]);

  useEffect(() => {
    let alive = true;

    const loadrematchCandidate = async () => {
      if (!isCompleted || !isParticipant || !RematchOpponentId) {
        if (alive) setrematchCandidate(null);
        return;
      }

      try {
        const res = await checkersAPI.listChallenges();
        const items = Array.isArray(res?.data) ? res.data : [];

        const matchPair = [asNumber(match?.creator_id), asNumber(match?.opponent_id)].sort((a, b) => a - b).join(':');
        const found = items.find((item) => {
          if (!item || String(item.status) !== 'pending') return false;
          if (String(item.id) === String(match?.id)) return false;
          if (String(item.mode || '') !== RematchMode) return false;
          if (asNumber(item.wager) !== RematchWager) return false;
          if (asNumber(item.turn_timeout_seconds) !== RematchTimeControlSeconds) return false;
          const itemPair = [asNumber(item.creator_id), asNumber(item.opponent_id)].sort((a, b) => a - b).join(':');
          return itemPair === matchPair;
        });
        if (alive) setrematchCandidate(found);
      } catch {
        if (alive) setrematchCandidate(null);
      }
    };

    loadrematchCandidate();
    const id = setInterval(loadrematchCandidate, 3000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [isCompleted, isParticipant, RematchOpponentId, RematchMode, RematchWager, RematchTimeControlSeconds, match?.id, match?.creator_id, match?.opponent_id]);

  const handleRematchCreate = async () => {
    if (!RematchOpponentId) {
      setError('Rematch is only available after both players are assigned.');
      return;
    }

    try {
      setrematchBusy(true);
      setError('');
      await checkersAPI.createMatch({
        mode: RematchMode,
        wager: RematchWager,
        turnTimeoutSeconds: RematchTimeControlSeconds,
        opponentId: RematchOpponentId,
      });
      refreshUser?.();
      // Stay on this page — the rematch candidate will appear via polling.
      // The creator will see "Open Rematch" once the opponent accepts.
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create Rematch challenge');
    } finally {
      setrematchBusy(false);
    }
  };

  const handleRematchAccept = async () => {
    if (!rematchCandidate) {
      setError('No Rematch challenge available yet.');
      return;
    }

    try {
      setrematchBusy(true);
      setError('');

      if (asNumber(rematchCandidate.opponent_id) === meId && !rematchCandidate.accepted_at) {
        await checkersAPI.acceptMatch(rematchCandidate.id);
      } else if (!rematchCandidate.opponent_id) {
        await checkersAPI.joinMatch(rematchCandidate.id);
      }

      refreshUser?.();
      navigate(`/checkers/duel/${rematchCandidate.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to accept Rematch challenge');
    } finally {
      setrematchBusy(false);
    }
  };

  const handleRematchDecline = async () => {
    if (!rematchCandidate) {
      setError('No Rematch challenge available yet.');
      return;
    }

    try {
      setrematchBusy(true);
      setError('');
      await checkersAPI.cancelMatch(rematchCandidate.id);
      setrematchCandidate(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to decline Rematch challenge');
    } finally {
      setrematchBusy(false);
    }
  };

  useEffect(() => {
    const tryAutoJoinFromInvite = async () => {
      if (!canJoinOpenRoom) return;
      if (inviteJoinAttemptedRef.current) return;
      inviteJoinAttemptedRef.current = true;

      try {
        setError('');
        await checkersAPI.joinMatch(matchId);
        refreshUser?.();
        await load();
      } catch (e) {
        const msg = String(e?.response?.data?.error || '').toLowerCase();
        if (msg.includes('full') || msg.includes('joined') || e?.response?.status === 409) {
          setError('This room is full. You can watch the game live.');
        } else {
          setError(e.response?.data?.error || 'Unable to join this room');
        }
      }
    };

    void tryAutoJoinFromInvite();
  }, [canJoinOpenRoom, matchId, refreshUser]);

  const onSquareClick = async (row, col) => {
    if (!match || match.status !== 'in_progress') return;
    if (!isMyTurn || submitting) return;

    const clicked = formatSquare(row, col);

    if (!selectedSquare) {
      const piece = board[row]?.[col];
      if (!piece) return;
      const color = String(piece).toLowerCase() === 'r' ? 'r' : 'b';
      if (color !== myColor) return;
      if (!legalMoves.some((m) => m.from === clicked)) return;
      setSelectedSquare(clicked);
      return;
    }

    if (clicked === selectedSquare) {
      setSelectedSquare(null);
      return;
    }

    if (!legalTargets.includes(clicked)) {
      const piece = board[row]?.[col];
      if (piece) {
        const color = String(piece).toLowerCase() === 'r' ? 'r' : 'b';
        if (color === myColor && legalMoves.some((m) => m.from === clicked)) {
          setSelectedSquare(clicked);
          return;
        }
      }
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // --- OPTIMISTIC UPDATE START ---
      const newBoard = JSON.parse(JSON.stringify(board));
      const piece = newBoard[selectedSquare.charCodeAt(0) - 97] ? null : null; // dummy to avoid unused
      // Find indices for fromSquare
      const fCol = selectedSquare.charCodeAt(0) - 97;
      const fRow = 8 - parseInt(selectedSquare.substring(1));
      const p = newBoard[fRow][fCol];
      
      newBoard[fRow][fCol] = null;
      newBoard[row][col] = p;

      // Handle Kinging
      if (p === 'r' && row === 0) newBoard[row][col] = 'R';
      if (p === 'b' && row === 7) newBoard[row][col] = 'B';

      // Handle simple jump capture
      if (Math.abs(row - fRow) === 2) {
        newBoard[(fRow + row) / 2][(fCol + col) / 2] = null;
      }

      const optimisticMatch = { 
        ...match, 
        board_state: newBoard,
        turn: match.turn === 'r' ? 'b' : 'r' // Optimistically flip turn unless must jump again
      };
      setMatch(optimisticMatch);
      setSelectedSquare(null);
      // --- OPTIMISTIC UPDATE END ---

      const res = await checkersAPI.submitMove(matchId, { from: selectedSquare, to: clicked });
      setMatch(res.data?.match || match);
      if (typeof res.data?.match?.server_now_ms === 'number') {
        lastServerOffsetRef.current = res.data.match.server_now_ms - Date.now();
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit move');
      setSelectedSquare(null);
      await load(); // sync back on error
    } finally {
      setSubmitting(false);
    }
  };

  if (!match && loading) {
    return (
      <div className="chess-match-page">
        <header className="cm-topbar">
          <button className="cm-icon-btn" onClick={() => navigate('/checkers/lobby')} title="Lobby">
            <span>←</span>
          </button>
          <div className="cm-topbar-center">
            <span className="cm-topbar-title">Checkers #{matchId}</span>
          </div>
        </header>
        <div className="cm-loading">Loading match data…</div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="chess-match-page">
        <header className="cm-topbar">
          <button className="cm-icon-btn" onClick={() => navigate('/checkers/lobby')} title="Lobby">
            <span>←</span>
          </button>
        </header>
        <div className="cm-toast cm-toast--error">{error || 'Match not found'}</div>
      </div>
    );
  }

  return (
    <div className="chess-match-page">
      {showWinnerConfetti && <Confetti recycle={false} numberOfPieces={220} />}

      {/* ————— TOP BAR ————— */}
      <header className="cm-topbar">
        <button className="cm-icon-btn" onClick={() => navigate('/checkers/lobby')} title="Lobby">
          <span>←</span>
        </button>
        <div className="cm-topbar-center">
          <span className="cm-topbar-title">Checkers #{matchId}</span>
          {match?.mode && <span className="cm-badge">{match.mode}</span>}
          {asNumber(match?.wager) > 0 && <span className="cm-badge cm-badge--gold">{match.wager} wkp</span>}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            className="cm-icon-btn"
            onClick={() => setSoundEnabled((prev) => !prev)}
            title={soundEnabled ? 'Mute' : 'Unmute'}
          >
            <span>{soundEnabled ? '🔊' : '🔇'}</span>
          </button>
          <button className="cm-icon-btn" onClick={() => navigate('/dashboard')} title="Dashboard">
            <span>🏠</span>
          </button>
        </div>
        </div>
      </header>

      {error && !isReconnecting && <div className="cm-toast cm-toast--error">{error}</div>}

      {/* ————— TOP PLAYER BAR (Black) ————— */}
      <div className={`cm-player-bar${match?.turn === 'b' && isInProgress ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">⚫</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="cm-player-bar__name">{topPlayerName}</span>
            {asNumber(match?.black_streak) >= 3 && (
              <span style={{ fontSize: 11 }}>🔥 {match.black_streak}</span>
            )}
          </div>
        </div>
        <div className={`cm-clock${match?.turn === 'b' && isInProgress ? ' cm-clock--ticking' : ''}`}>
          {match?.turn === 'b' && turnTimeLeftMs !== null ? formatTimer(turnTimeLeftMs) : turnTimeout > 0 ? `${turnTimeout}s` : '--:--'}
        </div>
      </div>

      {/* ————— BOARD ————— */}
      <div className="ck-board-wrap">
        {loading && <div className="cm-loading">Loading…</div>}
        {!loading && !!board.length && (
          <div className="checkers-board">
            {board.map((row, rIdx) => row.map((piece, cIdx) => {
              const square = formatSquare(rIdx, cIdx);
              const isDark = (rIdx + cIdx) % 2 === 0;
              const isSelected = selectedSquare === square;
              const isTarget = legalTargets.includes(square);
              const meta = pieceMeta(piece);
              return (
                <button
                  key={square}
                  type="button"
                  className={[
                    'checkers-square',
                    isDark ? 'checkers-square--dark' : 'checkers-square--light',
                    isSelected ? 'checkers-square--selected' : '',
                    isTarget ? 'checkers-square--target' : '',
                  ].join(' ')}
                  onClick={() => onSquareClick(rIdx, cIdx)}
                  disabled={!isDark}
                >
                  {meta && (
                    <span className={`checkers-piece checkers-piece--${meta.color}`}>
                      {meta.king ? 'k' : ''}
                    </span>
                  )}
                </button>
              );
            }))}
          </div>
        )}
      </div>

      {/* ————— BOTTOM PLAYER BAR (Red) ————— */}
      <div className={`cm-player-bar${match?.turn === 'r' && isInProgress ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">🔴</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="cm-player-bar__name">{bottomPlayerName}</span>
            {asNumber(match?.red_streak) >= 3 && (
              <span style={{ fontSize: 11 }}>🔥 {match.red_streak}</span>
            )}
          </div>
        </div>
        <div className={`cm-clock${match?.turn === 'r' && isInProgress ? ' cm-clock--ticking' : ''}`}>
          {match?.turn === 'r' && turnTimeLeftMs !== null ? formatTimer(turnTimeLeftMs) : turnTimeout > 0 ? `${turnTimeout}s` : '--:--'}
        </div>
      </div>

      {/* ————— RESULT BANNER ————— */}
      {isCompleted && (
        <div className={`cm-result-banner${didIWin ? ' cm-result-banner--win' : didILose ? ' cm-result-banner--loss' : ' cm-result-banner--draw'}`}>
          <span className="cm-result-banner__icon">{didIWin ? '🏆' : didILose ? '💔' : '🤝'}</span>
          <span className="cm-result-banner__text">
            {winnerId ? `Winner: ${winnerId === asNumber(match.red_id) ? redName : blackName}` : 'Draw'}
          </span>
          {asNumber(match?.wager) > 0 && (
            <span className="cm-result-banner__payout">
              Pot {asNumber(match.wager) * 2} • Charge {Number.isFinite(Number(match.charge_amount)) ? match.charge_amount : Math.floor(asNumber(match.wager) * 2 * 0.07)} • Net {Number.isFinite(Number(match.net_pot)) ? match.net_pot : Math.max(0, Math.floor(asNumber(match.wager) * 2 * 0.93))}
            </span>
          )}
        </div>
      )}

      {/* ————— STATUS LINE ————— */}
      {isInProgress && (
        <div className="cm-status-line">
          {isMyTurn ? '⚡ Your turn' : '⌛ Waiting for opponent…'}
          {submitting && ' — Sending move…'}
          {match?.must_continue_from && ` — Must continue from ${match.must_continue_from}`}
        </div>
      )}

        {isPending && (
          <div className="cm-status-line">
            Waiting for opponent
            {inviteLink && (
              <button className="cm-link-btn" onClick={copyInviteLink}>📋 Copy invite link</button>
            )}
          </div>
        )}

      {isRoomFullForViewer && (
        <div className="cm-status-line">Room full — watch mode</div>
      )}



      {/* â”€â”€â”€â”€â”€ ACTIONS â”€â”€â”€â”€â”€ */}
      <div className="cm-actions">
        {canAccept && (
          <button className="cm-btn cm-btn--primary" onClick={async () => {
            try { setError(''); await checkersAPI.acceptMatch(matchId); refreshUser?.(); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to accept'); }
          }}>Accept Challenge</button>
        )}

        {canJoinOpenRoom && (
          <button className="cm-btn cm-btn--primary" onClick={async () => {
            try {
              setError('');
              await checkersAPI.joinMatch(matchId);
              refreshUser?.();
              await load();
            } catch (e) {
              const msg = String(e?.response?.data?.error || '').toLowerCase();
              if (msg.includes('full') || msg.includes('joined') || e?.response?.status === 409) {
                setError('This room is full. You can watch the game live.');
              } else {
                setError(e.response?.data?.error || 'Failed to join room');
              }
            }
          }}>Join Challenge</button>
        )}

        {canCancel && (
          <button className="cm-btn cm-btn--ghost" onClick={async () => {
            try { setError(''); await checkersAPI.cancelMatch(matchId); refreshUser?.(); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to cancel'); }
          }}>Cancel</button>
        )}

        {canResign && (
          <button className="cm-btn cm-btn--danger" onClick={async () => {
            try { setError(''); await checkersAPI.resign(matchId); refreshUser?.(); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to resign'); }
          }}>Resign</button>
        )}

        {isInProgress && (
          <button className="cm-btn cm-btn--ghost" onClick={async () => {
            try { setError(''); await checkersAPI.claimTimeout(matchId); refreshUser?.(); await load(); }
            catch (e) { setError(e.response?.data?.error || 'Failed to claim timeout'); }
          }}>⏲ Claim Timeout</button>
        )}

        {canCreateRematch && (
          <button
            className="cm-btn cm-btn--primary"
            disabled={rematchBusy}
            onClick={handleRematchCreate}
          >
            {rematchBusy ? 'Sending…' : `🔄 Rematch${RematchWager > 0 ? ` (${RematchWager})` : ''}`}
          </button>
        )}

        {canStart && (
          <button
            className="cm-btn cm-btn--primary"
            onClick={async () => {
              try {
                setError('');
                await checkersAPI.startMatch(matchId);
                refreshUser?.();
                await load();
              } catch (e) {
                setError(e.response?.data?.error || 'Failed to start match');
              }
            }}
          >
            ▶ Start Match
          </button>
        )}

        {isCompleted && isParticipant && rematchCandidate && (
          <>
            <button
              className="cm-btn cm-btn--primary"
              disabled={rematchBusy}
              onClick={canAcceptRematch ? handleRematchAccept : () => navigate(`/checkers/duel/${rematchCandidate.id}`)}
            >
              {rematchBusy
                ? 'Wait…'
                : canAcceptRematch
                  ? `✅ Accept Rematch${asNumber(rematchCandidate.wager) > 0 ? ` (${asNumber(rematchCandidate.wager)})` : ''}`
                  : '🔄 Open Rematch'}
            </button>
            {canAcceptRematch && (
              <button
                className="cm-btn cm-btn--ghost"
                disabled={rematchBusy}
                onClick={handleRematchDecline}
              >
                Decline
              </button>
            )}
          </>
        )}

        {isCompleted && (
          <button className="cm-btn cm-btn--ghost" onClick={() => navigate('/checkers/lobby')}>
            Back to Lobby
          </button>
        )}
      </div>

      <QuickChat
        socket={checkersDuelSocket.socket || (typeof getSocket === 'function' ? getSocket() : null)}
        roomId={matchId}
        meId={user?.id}
        gameType="checkers"
      />
    </div>
  );
}



